<script setup lang="ts" generic="T extends NxTotalsRow">
/**
 * The rows of a totals block — the Summen of a document: a label on the left,
 * the figure in one column on the right edge, and in between the controls a
 * row may carry (a discount's amount and percent fields). Every row is as tall
 * as a large control, so plain rows and rows with fields run in one even
 * rhythm, and the figure column has one width, so several lists stacked in a
 * column (groups set apart by the container's gap) read down one edge.
 *
 * `rows` hold `{ key, label?, sub? }` and whatever the slots need. The label is
 * the row's `label`, or the slot `label` for a richer one (a muted second line
 * under it). `sub` marks a part of the row above („davon …"): indented, in the
 * label colour. The slot `value` renders the figure (the kit's
 * NxTabularNumber). The slot `aside` renders the controls between label and
 * figure, side by side; a list with it gives every row that column and the
 * tighter padding of a row with fields. Every slot gets `{ row, index }`.
 */
import { useSlots } from 'vue';

defineProps<{
  rows: readonly T[];
}>();

defineSlots<{
  label?: (props: { row: T; index: number }) => unknown;
  aside?: (props: { row: T; index: number }) => unknown;
  value?: (props: { row: T; index: number }) => unknown;
}>();

const slots = useSlots();
</script>

<script lang="ts">
export interface NxTotalsRow {
  /** A stable key per row. */
  key: string | number;
  /** The label; the slot `label` replaces it. */
  label?: string;
  /** A part of the row above („davon …"): indented, in the label colour. */
  sub?: boolean;
}
</script>

<template>
  <div class="nx-totals" :class="{ 'has-aside': !!slots.aside }" data-testid="nx-totals-list">
    <div
      v-for="(row, index) in rows"
      :key="row.key"
      class="nx-totals__row"
      data-testid="nx-totals-row"
    >
      <span class="nx-totals__label" :class="{ 'is-sub': row.sub }">
        <slot name="label" :row="row" :index="index">{{ row.label }}</slot>
      </span>
      <span v-if="slots.aside" class="nx-totals__aside">
        <slot name="aside" :row="row" :index="index" />
      </span>
      <span class="nx-totals__value">
        <slot name="value" :row="row" :index="index" />
      </span>
    </div>
  </div>
</template>

<style scoped>
.nx-totals {
  /* One figure column for every list of the block, so the figures stack on
     the right edge. */
  --totals-value: 8.5em;

  display: flex;
  flex-direction: column;
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-sm);
}

/* Every row is one height — a row with fields sets it, the plain rows take
   it — so the figures run in one even rhythm instead of tight pairs between
   taller rows. */
.nx-totals__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) var(--totals-value);
  align-items: center;
  column-gap: var(--nxt-space-sm);
  min-height: var(--nxt-control-size-lg);
  padding-block: var(--nxt-space-2xs);
  line-height: var(--nxt-line-height-tight);
}

/* label | the controls | figure. The label takes what the controls leave and
   wraps rather than being cut. */
.nx-totals.has-aside .nx-totals__row {
  grid-template-columns: minmax(0, 1fr) auto var(--totals-value);
  padding-block: 0.1em;
}

.nx-totals__label {
  min-width: 0;
}

.nx-totals__label.is-sub {
  padding-inline-start: var(--nxt-space-sm);
  color: var(--nxt-label-fg);
}

.nx-totals__aside {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-xs);
}

.nx-totals__value {
  text-align: end;
  justify-self: stretch;
  min-width: 0;
}
</style>
